// What? A collapsible outline of the document's sections.
// Why? Both structure views are long pages with no way to see the hierarchy at
//      a glance or jump between sections.
// How? A layout column, not an overlay. `position: sticky` keeps it pinned in
//      the same window position while the page scrolls, while still occupying
//      space — so it can never cover the content, the source-verification
//      column or the floating chat button. This is the convention documentation
//      sites use for "On this page" rails.
//
//      Below the `sideColumnFrom` breakpoint the rail drops to full width
//      above the content and starts collapsed, the way a markdown table of
//      contents behaves on mobile. Hosts whose width is already divided
//      between other panels pass "never" to keep it stacked at every size.
//
//      The host view places this inside a flex row; see StructureTab and
//      GenerationView.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, IconButton, Sheet, Tooltip, Typography } from "@mui/joy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

import { UPPERBAR_HEIGHT_PX } from "../../../../../components/Upperbar";
import { NAV_SECTION_ATTR } from "./types";
import type { StructureNavNode } from "./types";
import { collectParentNavIds, flattenNavIds } from "./buildNavTree";
import { useActiveSection } from "./useActiveSection";

const PANEL_WIDTH = 276;
const COLLAPSED_WIDTH = 44;
const TOP_PX = UPPERBAR_HEIGHT_PX + 16;

/** Breakpoints the rail can turn into a side column at, in px. */
const SIDE_COLUMN_BREAKPOINTS = { md: 900, lg: 1200 } as const;

export type SideColumnFrom = keyof typeof SIDE_COLUMN_BREAKPOINTS | "never";

export interface StructureNavigatorProps {
  nodes: StructureNavNode[];
  /** localStorage key holding whether the outline is open. */
  storageKey: string;
  /** Sticky header the scroll target must clear, measured at click time. */
  scrollOffsetRef?: React.RefObject<HTMLElement | null>;
  /** Breathing room above the scrolled-to section. */
  extraScrollOffsetPx?: number;
  /** Runs after scrolling, e.g. to focus the section's title input. */
  onNavigate?: (id: string) => void;
  title?: string;
  /**
   * Width at which the rail becomes a side column beside the content.
   *
   * Use "never" where the host already splits the width between other panels:
   * a rail there would compete for space the content cannot spare, so the
   * outline stays a collapsed strip above the content at every width.
   */
  sideColumnFrom?: SideColumnFrom;
}

const matches = (query: string): boolean => {
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
};

/**
 * Open by default only where the rail has a column of its own; collapsed
 * otherwise, so the outline never pushes the content down. An explicit choice
 * always wins over the default.
 */
const readOpen = (storageKey: string, isSideColumn: boolean): boolean => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch {
    // Private mode / storage disabled — fall through to the default.
  }
  return isSideColumn;
};

export const StructureNavigator: React.FC<StructureNavigatorProps> = ({
  nodes,
  storageKey,
  scrollOffsetRef,
  extraScrollOffsetPx = 16,
  onNavigate,
  title = "Outline",
  sideColumnFrom = "md",
}) => {
  // null when the rail never becomes a side column, so every layout decision
  // below collapses to the stacked-above-content case.
  const sideColumnQuery =
    sideColumnFrom === "never"
      ? null
      : `(min-width: ${SIDE_COLUMN_BREAKPOINTS[sideColumnFrom]}px)`;
  const breakpoint = sideColumnFrom === "never" ? null : sideColumnFrom;
  const isSideColumn = sideColumnQuery ? matches(sideColumnQuery) : false;

  const [isOpen, setIsOpen] = useState(() =>
    readOpen(storageKey, isSideColumn),
  );
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const activeItemRef = useRef<HTMLDivElement | null>(null);
  // Height of the host's sticky header, so the rail parks beneath it rather
  // than sliding underneath. Responsive, so it is measured rather than assumed.
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const el = scrollOffsetRef?.current;
    if (!el) return;

    const measure = () => setHeaderHeight(el.getBoundingClientRect().height);
    measure();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollOffsetRef]);

  const stickyTop = TOP_PX + headerHeight;

  const navIds = useMemo(() => flattenNavIds(nodes), [nodes]);
  const parentIds = useMemo(() => collectParentNavIds(nodes), [nodes]);

  // Chrome above the content: fixed upperbar + whatever sticky header the host
  // view pins beneath it. Measured lazily because the header height is responsive.
  const measureOffset = useCallback(
    () =>
      UPPERBAR_HEIGHT_PX +
      (scrollOffsetRef?.current?.getBoundingClientRect().height ?? 0) +
      extraScrollOffsetPx,
    [scrollOffsetRef, extraScrollOffsetPx],
  );

  const activeId = useActiveSection(navIds, UPPERBAR_HEIGHT_PX + headerHeight);

  const setOpen = useCallback(
    (next: boolean) => {
      setIsOpen(next);
      try {
        localStorage.setItem(storageKey, next ? "1" : "0");
      } catch {
        // Persisting is a convenience; ignore storage failures.
      }
    },
    [storageKey],
  );

  const toggleNode = useCallback((id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleNavigate = useCallback(
    (id: string) => {
      const scrollToSection = () => {
        const el = document.querySelector<HTMLElement>(
          `[${NAV_SECTION_ATTR}="${id}"]`,
        );
        if (!el) return;

        const top =
          el.getBoundingClientRect().top + window.scrollY - measureOffset();
        window.scrollTo({
          top: Math.max(top, 0),
          behavior: matches("(prefers-reduced-motion: reduce)")
            ? "auto"
            : "smooth",
        });
      };

      // When the outline sits above the content rather than beside it, leaving
      // it open would strand the reader above the section they just picked.
      const stacked = !(sideColumnQuery && matches(sideColumnQuery));
      if (stacked) {
        // Collapsing the rail removes its height from *above* the target, so the
        // scroll target has to be measured after that layout change commits.
        // Measuring first overshoots by the open rail's height and lands a
        // paragraph or so past the section heading.
        setOpen(false);
        requestAnimationFrame(scrollToSection);
      } else {
        scrollToSection();
      }

      onNavigate?.(id);
    },
    [measureOffset, onNavigate, setOpen, sideColumnQuery],
  );

  // Keep the highlighted row visible inside the rail as the page scrolls; a
  // long outline scrolls internally, so the active section can drift offscreen.
  useEffect(() => {
    if (activeId && activeItemRef.current) {
      activeItemRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [activeId]);

  // Sections can disappear (deleted, or a fresh document); drop stale collapse state.
  useEffect(() => {
    setCollapsedIds((prev) => {
      if (!prev.size) return prev;
      const valid = new Set(parentIds);
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [parentIds]);

  const renderNode = (node: StructureNavNode, level: number) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = hasChildren && !collapsedIds.has(node.id);
    const isActive = node.id === activeId;

    return (
      <React.Fragment key={node.id}>
        <Box
          ref={isActive ? activeItemRef : undefined}
          role="treeitem"
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-selected={isActive}
          onClick={() => handleNavigate(node.id)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            py: 0.4,
            pr: 1,
            pl: level * 1.5 + 0.5,
            cursor: "pointer",
            borderRadius: "sm",
            bgcolor: isActive ? "primary.softBg" : "transparent",
            "&:hover": {
              bgcolor: isActive ? "primary.softHoverBg" : "neutral.softHoverBg",
            },
          }}
        >
          {hasChildren ? (
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              aria-label={isExpanded ? "Collapse section" : "Expand section"}
              onClick={(event) => {
                // Toggling the subtree must never navigate.
                event.stopPropagation();
                toggleNode(node.id);
              }}
              sx={{ minHeight: 20, minWidth: 20 }}
            >
              {isExpanded ? (
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              ) : (
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          ) : (
            // Placeholder keeps leaf labels aligned with their siblings'.
            <Box sx={{ width: 20, flexShrink: 0 }} />
          )}

          <Typography
            level="body-sm"
            noWrap
            title={node.title}
            sx={{
              color: isActive ? "primary.plainColor" : "text.secondary",
              fontWeight: isActive ? "lg" : "md",
              minWidth: 0,
            }}
          >
            {node.title}
          </Typography>
        </Box>

        {isExpanded && (
          <Box role="group">
            {node.children.map((child) => renderNode(child, level + 1))}
          </Box>
        )}
      </React.Fragment>
    );
  };

  if (!nodes.length) return null;

  return (
    <Box
      sx={{
        // Stacked above the content, becoming a right-hand column once the
        // configured breakpoint is reached (never, where breakpoint is null).
        order: breakpoint ? { xs: -1, [breakpoint]: 0 } : -1,
        width: breakpoint
          ? { xs: "100%", [breakpoint]: isOpen ? PANEL_WIDTH : COLLAPSED_WIDTH }
          : "100%",
        flexShrink: 0,
        mb: breakpoint ? { xs: 2, [breakpoint]: 0 } : 2,
        // Sticky needs a non-stretched flex item, and an ancestor that does not
        // clip overflow (see the Card overflow:visible in the host views).
        alignSelf: "flex-start",
        ...(breakpoint
          ? {
              position: { [breakpoint]: "sticky" },
              top: { [breakpoint]: `${stickyTop}px` },
            }
          : {}),
        transition: "width 0.2s ease-in-out",
      }}
    >
      {isOpen ? (
        <Sheet
          variant="outlined"
          sx={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: "md",
            maxHeight: breakpoint
              ? { xs: 260, [breakpoint]: `calc(100vh - ${stickyTop + 24}px)` }
              : 260,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "background.level1",
            }}
          >
            <Typography level="title-sm" noWrap>
              {title}
            </Typography>
            <Tooltip title="Hide outline" placement="left" arrow>
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                aria-label="Hide outline"
                onClick={() => setOpen(false)}
              >
                <ExpandMoreIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box role="tree" sx={{ flex: 1, overflowY: "auto", p: 0.5 }}>
            {nodes.map((node) => renderNode(node, 0))}
          </Box>
        </Sheet>
      ) : (
        // Anchored below rather than to the left: when collapsed on a narrow
        // screen this row spans the full width, so a left-placed tooltip lands
        // off-screen.
        <Tooltip title="Show outline" placement="bottom" arrow>
          <Sheet
            variant="outlined"
            role="button"
            tabIndex={0}
            aria-label="Show outline"
            onClick={() => setOpen(true)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setOpen(true);
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: breakpoint
                ? { xs: "space-between", [breakpoint]: "center" }
                : "space-between",
              gap: 1,
              px: breakpoint ? { xs: 1.5, [breakpoint]: 0 } : 1.5,
              py: 0.75,
              cursor: "pointer",
              borderRadius: "md",
              "&:hover": { bgcolor: "neutral.softHoverBg" },
            }}
          >
            {/* The label only earns its space in the full-width stacked row;
                the side column collapses to an icon-only strip. */}
            <Typography
              level="title-sm"
              noWrap
              sx={{
                display: breakpoint
                  ? { xs: "block", [breakpoint]: "none" }
                  : "block",
              }}
            >
              {title}
            </Typography>
            <AccountTreeIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          </Sheet>
        </Tooltip>
      )}
    </Box>
  );
};

export default StructureNavigator;
